import { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Modal } from 'antd';
import { Button } from '@components/common/Button';
import { LoadingButton } from '@components/common/LoadingButton/LoadingButton';
import { FormTextField } from '@components/common/FormTextField/FormTextField';
import { formatNoteDate } from '@utils/dates';
import { sanitizeAndParseHtml, sanitizeToPlainText } from '@utils/sanitizeHtml';

export interface FormattedNote {
  id: string;
  title: string;
  content: string;
  createdAt: Date;
  creator: string;
}

interface NotesForm {
  title: string;
  content: string;
}

interface NotesProps {
  notes: FormattedNote[];
  onCreateNote: (title: string, content: string) => Promise<void>;
  isLoading?: boolean;
}

export function Notes({ notes, onCreateNote, isLoading = false }: NotesProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const form = useForm<NotesForm>({
    defaultValues: {
      title: '',
      content: '',
    },
  });

  const handleCreate = async (data: NotesForm) => {
    if (!data.title.trim() || !data.content.trim() || isCreating) {
      return;
    }

    setIsCreating(true);
    try {
      // Sanitize both title and content to remove all HTML and keep only plain text
      const sanitizedTitle = sanitizeToPlainText(data.title);
      const sanitizedContent = sanitizeToPlainText(data.content);

      if (!sanitizedTitle || !sanitizedContent) {
        return;
      }

      await onCreateNote(sanitizedTitle, sanitizedContent);
      form.reset();
      setIsModalOpen(false);
    } catch (error) {
      console.error('Failed to create note:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    if (isCreating) return; // Prevent closing during creation
    setIsModalOpen(false);
    form.reset();
  };

  return (
    <div className='flex size-full flex-col' style={{ overflow: 'visible' }}>
      <div className={'mr-4 flex shrink-0 justify-end'}>
        <Button variant={'contained'} onClick={handleOpenModal} disabled={isLoading}>
          New Note
        </Button>
      </div>
      <Modal
        open={isModalOpen}
        onCancel={handleCloseModal}
        title={'Create Note'}
        footer={null}
        centered
        width={900}
        maskClosable={!isCreating}
        closable={!isCreating}
        classNames={{
          body: 'pt-2',
        }}
      >
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(handleCreate)}>
            <div className={'mt-2'}>
              <div className={'mb-4'}>
                <FormTextField name={'title'} textFieldProps={{ label: 'Title' }} disabled={isCreating || isLoading} />
              </div>
              <div className={'mb-4'}>
                <FormTextField
                  name={'content'}
                  textFieldProps={{ label: 'Content', multiline: true, rows: 4 }}
                  disabled={isCreating || isLoading}
                />
              </div>
            </div>
            <div className={'mt-6 flex justify-end gap-2'}>
              <Button onClick={handleCloseModal} disabled={isCreating || isLoading}>
                Cancel
              </Button>
              <LoadingButton
                isLoading={isCreating || isLoading}
                buttonProps={{
                  variant: 'contained',
                  type: 'submit',
                  disabled: isCreating || isLoading || !form.watch('title')?.trim() || !form.watch('content')?.trim(),
                }}
              >
                {isCreating || isLoading ? 'Creating...' : 'Create'}
              </LoadingButton>
            </div>
          </form>
        </FormProvider>
      </Modal>
      <div className={'mt-2 flex min-h-0 flex-1 flex-col rounded-lg border border-gray-300 px-4'}>
        {notes.length === 0 ? (
          <p className={'py-4 text-center text-gray-500'}>No notes yet</p>
        ) : (
          <div className={'custom-scrollbar h-full space-y-4 overflow-y-auto py-2'}>
            {notes.map(note => (
              <div key={note.id} className={'rounded-lg border border-gray-200 bg-white px-4 py-1'}>
                <div className={'mb-2 flex items-center justify-between'}>
                  <h4 className={'m-0 text-base font-semibold'}>{note.title}</h4>
                  <span className={'text-xs text-gray-500'}>
                    {formatNoteDate(note.createdAt)} by {note.creator.toLowerCase()}
                  </span>
                </div>
                <div className={'mb-2 text-sm text-gray-700'} style={{ wordBreak: 'break-word' }}>
                  {sanitizeAndParseHtml(note.content)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
